import { getIn, useFormikContext } from 'formik';
import FieldWrapper from '../../../components/formInputs/FieldWrapper';
import FormUpload from '../../../components/formInputs/FormUpload';
import { customStyles } from '../../../utils/SelectStyles';
import { ISingleOrderField, IStepData } from '../types/interfaces';
import { useMemo, useRef } from 'react';
import DatePickerField from '../../../components/formInputs/DatePickerField';
import { useTranslation } from 'react-i18next';
import FormGalleryUpload from '../../../components/formInputs/FormGalleryUpload';
import StepCommentsBtn from './StepCommentsBtn';

interface IProps {
  data: ISingleOrderField;
  entityId: number;
  formik?: any;
  disabled: boolean;
  setCurrOpenField: React.Dispatch<React.SetStateAction<ISingleOrderField | null>>;
  currOpenField: ISingleOrderField | null;
  closeCommentsSide: () => void;
  animationRef: any;
  sidebarRef: any;
  canApproveStep: boolean;
  stepData: IStepData
}

const SingleOrderField = ({ data, entityId, disabled, setCurrOpenField, currOpenField, closeCommentsSide, animationRef, sidebarRef, canApproveStep }: IProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const { t, i18n } = useTranslation();
  const formik = useFormikContext();

  const { selectOptions, inputName } = useMemo(() => {
    let selectOptions: any = [];
    if (data?.validation?.options) selectOptions = data?.validation?.options?.map((option) => ({ label: option, value: option }));

    const inputName = `entities.entity_${entityId}.values.field_${data?.id}.value`;

    return { selectOptions, inputName };
  }, [data, entityId]);

  const { canShowComments } = useMemo(() => {
    const canShowComments = canApproveStep || !!data?.comments?.length;
    return { canShowComments };
  }, [canApproveStep, data]);

  return (
    <div
      className={`single-order-field ${data?.type === 'file' ? 'file' : ''}`}
      style={{ ...(data?.type === 'gallery' ? { gridColumn: 'span 3' } : {}) }}
    >
      <div className='header'>
        <div className={`header-content`}>
          <h5 className="title">{data?.label?.[`${i18n?.language as 'en' | 'ar'}`]}</h5>
          {data?.changed && <div className='badge current'>{t('changed')}</div>}
        </div>
        {canShowComments && (
          <StepCommentsBtn
            currOpenField={currOpenField}
            setCurrOpenField={setCurrOpenField}
            field={data}
            closeCommentsSide={closeCommentsSide}
            sidebarRef={sidebarRef}
            animationRef={animationRef}
          />
        )}
      </div>
      {(data?.type === 'text' || data?.type === 'string' || data?.type === 'number' || data?.type === 'textarea') && (
        <FieldWrapper
          // title={data?.label?.[`${i18n?.language as 'en' | 'ar'}`]}
          noPadding
          inputName={inputName}
          inputPlaceholder={data?.label?.[`${i18n?.language as 'en' | 'ar'}`]}
          input={!(data?.type === 'textarea')}
          textArea={data?.type === 'textarea'}
          ref={inputRef}
          disabled={disabled}
          type={data?.type}
          rows={11}
        />
      )}
      {data?.type === 'select' && (
        <FieldWrapper
          noPadding
          // title={data?.label?.[`${i18n?.language as 'en' | 'ar'}`]}
          disabled={disabled}
          inputPlaceholder={data?.label?.[`${i18n?.language as 'en' | 'ar'}`]}
          inputName={inputName}
          options={selectOptions}
          value={selectOptions.find((ele: any) => ele.value === getIn(formik.values, inputName))}
          selectStyle={customStyles}
          select
        />
      )}
      {(data?.type === 'file' || data?.type === 'document') && (
        <FormUpload formik={formik} name={inputName} fileName={data?.label?.[`${i18n?.language as 'en' | 'ar'}`]} disabled={disabled} file />
      )}
      {data?.type === 'gallery' && <FormGalleryUpload disabled={disabled} formik={formik} name={inputName} />}
      {data?.type === 'boolean' && (
        <FieldWrapper
          disabled={disabled}
          formik={formik}
          inputName={inputName}
          value={getIn(formik?.values, inputName)}
          switchInp
          onChange={(e: any) => formik?.setFieldValue(inputName, e?.target?.checked ? 1 : 0)}
          noPadding
        />
      )}
      {data?.type === 'date' && <DatePickerField title="" name={inputName} placeHolder="DD/MM/YYYY" noPadding disabled={disabled} />}
    </div>
  );
};

export default SingleOrderField;
